import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input, SearchResult } from '../';
import { useDebounce, useSearch } from '../../hooks';
import { keyCode, formatData } from '../../helpers';

import './SearchBar.css';

export const SearchBar = () => {
  const [showSearchResultDropdown, setShowSearchResultDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [itemIsClicked, setItemIsClicked] = useState(false);

  const {
    searchTerm,
    searchResults,
    searchResultsCount,
    setSearchTerm,
    fetchSearchResults,
    clearSearchResults,
    invalidSearchTerm,
    loadNextPage,
  } = useSearch({ setLoading });

  const refSearchInput = useRef();
  const refViewAll = useRef();
  const refSearchResultDropdown = useRef();
  const refSearchResults = useRef([]);

  const navigate = useNavigate();

  // Typing a search term used to issue one request per keystroke, each returning a full page of
  // results, so "customer country" was sixteen searches of which only the last was wanted. The input
  // and the dropdown still react immediately; only the request waits for a pause in typing.
  // The ref keeps one stable timer across renders while always calling the current fetch closure —
  // useSearch returns a new function on every render, and a new timer per render would not debounce.
  const refFetchSearchResults = useRef(fetchSearchResults);
  refFetchSearchResults.current = fetchSearchResults;

  const { debounce: debouncedFetchSearchResults } = useDebounce(
    value => refFetchSearchResults.current(value, false, 1),
    200,
    []
  );

  useEffect(() => {
    // Scroll to top if search term is changed
    if (refSearchResultDropdown.current) {
      refSearchResultDropdown.current.scrollTop = 0;
    }
  }, [refSearchResultDropdown.current, searchTerm]);

  const handleInputChange = event => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    setItemIsClicked(false);
    setSearchTerm(value);

    if (value.length > 1) {
      debouncedFetchSearchResults(value);
      setShowSearchResultDropdown(true);
    } else {
      setShowSearchResultDropdown(false);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const onKeyDown = e => {
    if ((e.ctrlKey || e.metaKey) && e.keyCode === keyCode.K) {
      e.preventDefault();
      refSearchInput.current.focus();
    }

    if (e.keyCode === keyCode.ESCAPE) {
      setTimeout(() => refSearchInput.current.blur(), 100);
      setShowSearchResultDropdown(false);
    }
  };

  const handleKeyDown = (e, value) => {
    switch (e.keyCode) {
      case keyCode.ENTER:
        if (searchTerm && searchTerm.length > 1) {
          navigate('/?search=' + searchTerm);
        } else {
          e.preventDefault();
        }
        break;
      default:
        break;
    }
  };

  const clearSearch = () => {
    refSearchInput.current.focus();
    setLoading(false);
    clearSearchResults();
  };

  const handleFocus = event => {
    setItemIsClicked(false);
    if (searchTerm && searchTerm.length > 1) {
      setShowSearchResultDropdown(true);
    }
  };

  const handleBlur = el => {
    const activeElement = el.relatedTarget;

    if (
      activeElement === null ||
      !(
        activeElement === refSearchInput ||
        refSearchResults.current.findIndex(el => el === activeElement) >= 0 ||
        activeElement === refViewAll.current
      )
    ) {
      setShowSearchResultDropdown(false);
    }
  };

  const handleSelect = () => {
    setItemIsClicked(prev => true);
  };

  const loadMore = () => {
    loadNextPage();
  };

  const hideDropdown = el => {
    if (itemIsClicked) {
      setShowSearchResultDropdown(false);
    }
  };

  return (
    <div
      className={'SearchBar slim' + (invalidSearchTerm ? ' invalid' : '')}
      onMouseLeave={hideDropdown}
    >
      <form autoComplete="off">
        <Input
          type="text"
          name="search"
          ref={refSearchInput}
          placeholder="Search Katalogue..."
          value={searchTerm}
          icon="search"
          validation={invalidSearchTerm && { valid: false, message: 'Invalid search string' }}
          isNullable={true}
          keyboardShortcut="search"
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      </form>

      <div
        className={
          'search-result-dropdown' +
          (showSearchResultDropdown && searchResults && searchTerm.length > 1 ? ' show' : '')
        }
      >
        {searchResultsCount && searchResultsCount.all > 0 && (
          <div className="search-result-dropdown-header">
            {<span>{formatData(searchResultsCount.all, 'integer')} assets found</span>}
            <Link className="link" to={'/?search=' + searchTerm} ref={refViewAll}>
              View detailed results
            </Link>
          </div>
        )}
        <div className="search-result-dropdown-content" ref={refSearchResultDropdown}>
          <SearchResult
            data={searchResults}
            ref={refSearchResults}
            loadMore={loadMore}
            containerElement={refSearchResultDropdown.current}
            resultCount={searchResultsCount && searchResultsCount.all}
            loading={loading}
            searchTerm={searchTerm}
            onClick={handleSelect}
          />
        </div>
      </div>
    </div>
  );
};
